import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { CITY } from '@city'
import { StoreLinks } from '../../scripts/storeLinks.mjs'
import type { StoreSlot } from '../../scripts/storeLinks.mjs'
import { TrackingLinks } from '../../scripts/trackingLinks.mjs'
import { isProductionEnvironment } from '../analytics/environment'
import { reportStoreOutboundTapped, storeKindForUserAgent } from '../analytics/storeOutboundEvents'
import { waitForAmplitudeDeviceId } from '../analytics/waitForAmplitudeDeviceId'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { storeCtaHref } from '../components/storeCtaHref'
import { useSchemaOptional } from '../components/useSchema'
import { whenConsentWindowSettled } from '../cookie/consentWindowSettled'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import { buildSlugIndex } from '../state/slugIndex'
import { useMetroMapStore } from '../state/useMetroMapStore'
import { DesktopBenefitsPrompt } from './DesktopBenefitsPrompt'
import { DesktopPhonePrompt } from './DesktopPhonePrompt'
import { PhoneHomePrompt } from './PhoneHomePrompt'
import { PhoneRouteAPrompt } from './PhoneRouteAPrompt'
import { PhoneRouteBPrompt } from './PhoneRouteBPrompt'
import {
  reportPromptCtaTapped,
  reportPromptDismissed,
  reportPromptShown,
  trackExposure,
} from './promptAnalytics'
import { choosePrompt, previewChoice } from './promptChoice'
import type { PromptChoice } from './promptChoice'
import { PROMPT_DEPLOYMENT_KEY, fetchPromptVariant } from './promptExperiment'
import type { PromptFlag } from './promptExperiment'
import { promptAppLink } from './promptAppLink'
import { markPromptShown, promptAlreadyShown } from './promptOncePerVisit'
import { promptPhone, promptSurface } from './promptSurface'
import { routeDestination } from './routeDestination'
import { storeRating } from './storeRating'

/**
 * The app prompt on entry — the site's first A/B test (2026-11-03).
 *
 * Once per visit, after the cookie window is answered (one modal at a
 * time), the visitor's surface is decided — a phone on a route page with
 * its route shown, a phone anywhere else, a computer — and its Amplitude
 * flag names the arm. No arm (flag off, request failed, no Amplitude device
 * id to bucket by) → no prompt. Shown to every visitor, crawlers included:
 * hiding it from bots would serve them a different page (cloaking).
 *
 * Production only, except `?app_prompt=<key>` (`previewChoice`), which shows
 * a prompt anywhere with no flag request, gate or analytics.
 */
export interface AppPromptHostProps {
  readonly reporter?: MetroAnalyticsReporter
  /** Seams for tests; production uses the defaults. */
  readonly userAgent?: string
  readonly location?: Pick<Location, 'pathname' | 'search'>
  readonly isProduction?: () => boolean
  readonly consentSettled?: () => Promise<void>
  readonly deviceId?: () => Promise<string | undefined>
  readonly readVariant?: (flag: PromptFlag, deviceId: string) => Promise<string | undefined>
}

/** How long, from entry, a route page waits for its route before showing the home prompt. */
const ROUTE_WAIT_MS = 10_000

const defaultReadVariant = (flag: PromptFlag, deviceId: string) =>
  fetchPromptVariant(flag, deviceId, PROMPT_DEPLOYMENT_KEY)

function slotFor(choice: PromptChoice): StoreSlot {
  switch (choice.kind) {
    case 'home':
      return StoreLinks.SLOTS.APP_PROMPT_HOME
    case 'route':
      return choice.variant === 'a'
        ? StoreLinks.SLOTS.APP_PROMPT_ROUTE_A
        : StoreLinks.SLOTS.APP_PROMPT_ROUTE_B
    case 'desktop':
      return choice.variant === 'v1'
        ? StoreLinks.SLOTS.APP_PROMPT_DESKTOP_1
        : StoreLinks.SLOTS.APP_PROMPT_DESKTOP_2
  }
}

export function AppPromptHost({
  reporter = realMetroAnalyticsReporter,
  userAgent = navigator.userAgent,
  location = window.location,
  isProduction = isProductionEnvironment,
  consentSettled = whenConsentWindowSettled,
  deviceId = waitForAmplitudeDeviceId,
  readVariant = defaultReadVariant,
}: AppPromptHostProps = {}): ReactElement | null {
  const store = useMetroMapStore()
  // A preview is known from the URL alone, so it is the initial state.
  const schema = useSchemaOptional()
  // The visitor's Amplitude device id, carried on the app link (`wd`).
  const [visitorDeviceId, setVisitorDeviceId] = useState<string | undefined>()
  const [choice, setChoice] = useState<PromptChoice | undefined>(() =>
    previewChoice(
      location.search,
      promptPhone(userAgent),
      store.getState().routeSelection.toStation?.localName,
    ),
  )

  // Decided once, on entry: the page the visitor landed on, not later
  // in-app navigation, is what the surface and the test are about.
  useEffect(() => {
    let cancelled = false
    const entryPathname = location.pathname
    if (previewChoice(location.search, undefined, undefined)) return
    if (!isProduction() || promptAlreadyShown()) return

    void (async () => {
      // The route loads while the cookie window is open: wait for both at
      // once, so a slow first load is not cut short by time spent on cookies.
      const destinationOnEntry = routeDestination(store, entryPathname, ROUTE_WAIT_MS)
      await consentSettled()
      const destination = await destinationOnEntry
      const surface = promptSurface(userAgent, entryPathname, destination)
      const id = await deviceId()
      if (!id || cancelled) return
      const picked = await choosePrompt(surface, (flag) => readVariant(flag, id))
      if (!picked || cancelled || promptAlreadyShown()) return
      markPromptShown()
      setVisitorDeviceId(id)
      setChoice(picked)
    })()
    return () => {
      cancelled = true
    }
    // Every dependency is fixed for the page's life (module functions, the
    // page's own location and user agent), so this runs once, on entry.
  }, [store, userAgent, location, isProduction, consentSettled, deviceId, readVariant])

  const isPreview = choice?.arm.experiment === 'preview'

  useEffect(() => {
    if (!choice || isPreview) return
    reportPromptShown(reporter, choice.arm)
    trackExposure(choice.arm)
  }, [choice, isPreview, reporter])

  const close = (action: 'back' | 'close') => {
    if (choice && !isPreview) reportPromptDismissed(reporter, choice.arm, action)
    setChoice(undefined)
  }
  const onBack = () => close('back')
  const onClose = () => close('close')

  /** The shown route's station slugs (the app's `/route?from=&to=`), when known. */
  const routeSlugs = () => {
    const { fromStation, toStation } = store.getState().routeSelection
    if (!schema || !fromStation || !toStation) return undefined
    const { slugById } = buildSlugIndex(schema)
    const from = slugById.get(fromStation.id)
    const to = slugById.get(toStation.id)
    return from && to ? { from, to } : undefined
  }

  if (!choice) return null

  if (choice.kind === 'desktop') {
    return choice.variant === 'v1' ? (
      <DesktopBenefitsPrompt onClose={onClose} />
    ) : (
      <DesktopPhonePrompt onClose={onClose} />
    )
  }

  const slot = slotFor(choice)
  const storeKind = storeKindForUserAgent(userAgent)
  const storeHref = CITY.appLinkHost
    ? promptAppLink({
        host: CITY.appLinkHost,
        source: TrackingLinks.siteSource(slot),
        deviceId: visitorDeviceId,
        route: choice.kind === 'route' ? routeSlugs() : undefined,
      })
    : storeCtaHref(userAgent, storeKind, slot)
  const onGetTheApp = () => {
    if (!isPreview) {
      reportPromptCtaTapped(reporter, choice.arm)
      reportStoreOutboundTapped(reporter, { store: storeKind, slot })
    }
    setChoice(undefined)
  }

  if (choice.kind === 'home') {
    return <PhoneHomePrompt storeHref={storeHref} onGetTheApp={onGetTheApp} onBack={onBack} />
  }
  return choice.variant === 'a' ? (
    <PhoneRouteAPrompt storeHref={storeHref} onGetTheApp={onGetTheApp} onBack={onBack} />
  ) : (
    <PhoneRouteBPrompt
      destination={choice.destination}
      rating={storeRating(choice.phone)}
      storeHref={storeHref}
      onGetTheApp={onGetTheApp}
      onBack={onBack}
    />
  )
}
